<template>
	<div class="container-fluid" style="padding: 0 8px;">
		<div class="grid-wrapper" v-if="this.group">
			<div cy-data-row="shopping-item" v-for="item in sellableItems" :key="item.id" class="">
				<div cy-data-row="item" class="item-container" @click="openItem(item)">
					<div class="image">
						<ding-image v-if="itemImagePath(item)" :src="context.storageEndpoint + itemImagePath(item)" aspect-ratio="1:1" width="100%"/>
						<div v-if="hasSoldOut(item)" class="icon-sold-out-container">
							<div class="icon-sold-out">{{$t('sold_out')}}</div>
						</div>
						<div v-if="hasReachedGroupLimit(item)" class="icon-sold-out-container">
							<div class="icon-sold-out">{{$t('reach_group_limit')}}</div>
						</div>
						<div v-if="notAvailableDueToPeriod(item)" class="icon-sold-out-container">
							<div class="icon-sold-out">{{$t('only_available_in_periods')}}</div>
						</div>
						<div v-if="notAvailableDueToWeekday(item)" class="icon-sold-out-container">
							<div class="icon-sold-out">{{$t('only_available_in_weekday')}}</div>
						</div>
						<div v-if="notAvailableDueToSpecFixedDate(item)" class="icon-sold-out-container">
							<div class="icon-sold-out">{{$t('not_available')}}</div>
						</div>
						<div v-if="notAvailableDueToIncludedInBookingPass(item)" class="icon-sold-out-container">
							<div class="icon-sold-out">{{$t('included_in_booking_pass')}}</div>
						</div>
					</div>
					<div class="py-1 px-2 item-name"><b>{{item.name}}</b></div>
					<div v-if="showRemainStock(item)" class="px-2 remaining-stock">
						<span class="mr-1">{{$t('remaining_label')}}</span><span>{{showRemainStock(item)}}</span>
					</div>
					<div class="flex-grow-1"></div>
					<div v-if="isTimeLimit(item)" class="pb-1 px-2">
						<div v-if="getAvailablePeriods(item).length > 0" class="d-flex">
							<div>
								<div v-for="(period, i) in getAvailablePeriods(item)" :key="i">
									{{$t('time_limit')}} {{availablePeriodsToDisplay(period)}}
								</div>
							</div>
						</div>
						<div v-if="getAvailableWeekdays(item).length > 0" class="d-flex flex-wrap">
							<div class="mr-1">{{$t('time_limit')}}</div>
							<div v-for="(weekday, id) in getAvailableWeekdays(item)" :key="weekday">
								<span>{{$t('message.weekday_' + weekday)}}</span><span v-if="(id + 1) < getAvailableWeekdays(item).length">{{$t('common')}}</span>
							</div>
						</div>
					</div>
					<div class="pb-1 px-2">
						<span :style="{color: provider.theme_tint_hex}"><b><buyable-price :provider="provider" :item="item"/></b></span>
					</div>
				</div>
			</div>
		</div>
		<item-viewer v-if="openedItem" :item="openedItem" :stock-info-map="stockInfoMap" @close="openedItem = null" :context="context" :provider="provider" :question-id="questionId" :item-group-id="itemGroupId"/>
	</div>
</template>

<script>
import moment from 'moment';
import {mapActions, mapGetters} from 'vuex';
import RootStore from '../stores/root';
import DingImage from '../components/common/Image';
import api from '../mixins/api';
import promptConfirm from '../mixins/prompt-confirm';
import BuyablePrice from './items/BuyablePrice';
import { EventViewItem } from '../bu/models/GA4';
import PricingRuleText from './items/PricingRuleText.vue';

export default {
	components: { DingImage, BuyablePrice, PricingRuleText },
	store: RootStore,
	mixins: [api, promptConfirm],
	props: {
		context: { type: Object, required: true},
		provider: { type: Object, required: true},
		itemGroupId: { type: Number, required: true},
		questionId: { type: Number, required: true},
		isRequired: { type: Boolean }
	},
	async mounted() {
		await this.prepareItemGroup();

		const url = new URL(window.location.href);
		const itemId = url.searchParams.get('item_id');
		const groupId = url.searchParams.get('item_group_id');

		if (itemId && groupId && groupId == this.itemGroupId) {
			const item = this.group.items.find(item => item.id == itemId);
			if (item) {
				this.openItem(item);
			}
		}
	},
	watch: {
		'context.selectedDate': {
			immediate: true,
			deep: true,
			handler: function(newValue, oldValue) {
				if (newValue == oldValue) {
					return;
				}
				if (newValue) {
					this.checkCartItemsStockAndAvailability();
				}
			}
		},
		'context.selectedEndDate': {
			immediate: true,
			deep: true,
			handler: function(newValue, oldValue) {
				if (newValue == oldValue) {
					return;
				}
				if (newValue) {
					this.checkCartItemsStockAndAvailability();
				}
			}
		},
		'context.selectedService': {
			immediate: true,
			deep: true,
			handler: function(newValue, oldValue) {
				if (newValue == oldValue) {
					return;
				}
				if (newValue?.provider_form_id && 
					oldValue?.provider_form_id && 
					newValue.provider_form_id == oldValue.provider_form_id
				) {
					this.prepareItemGroup();
				}
			}
		},
	},
	data() {
		return {
			openedItem: null,
			stockInfoMap: {
				specs: {},
				items: {}
			}
		}
	},
	computed: {
		...mapGetters({
			groupMap: 'item-groups/groupMap',
			allCartItems: 'cart/items',
			groupAmountLeftInfo: 'cart/groupAmountLeftInfo',
			bookingPassItem: 'cart/bookingPassItem'
		}),
		cartItems() {
			return this.allCartItems.filter((item) => item.itemId || item.specId);
		},
		group() {
			if (this.groupMap) {
				return this.groupMap[this.itemGroupId]
			}
			return null;
		},
		sellableItems() {
			if (this.context.selectedService && this.context.selectedService.allow_custom_duration) {
				return this.group.items.filter((item) => {
					return item.is_visible && (item.is_renting || !item.max_per_day);
				});
			}
			return this.group.items.filter((item) => item.is_visible);
		}
	},
	methods: {
		...mapActions({
			getItemGroup: 'item-groups/getItemGroup',
			setGroupRequired: 'item-groups/setGroupRequired',
			removeCartItemByItemIds: 'cart/removeCartItemByItemIds',
			removeCartItem: 'cart/removeCartItem',
			getFormBuyableAdvancedCap: 'cart/getFormBuyableAdvancedCap',
		}),
		notAvailableDueToIncludedInBookingPass(item) {
			const bookingPass = this.bookingPassItem?.bookingPass;
			const preset = bookingPass?.presets.length > 0 ? bookingPass.presets[0] : null;
			if (!preset || (!preset.provider_item_id && !preset.provider_item_spec_id)) {
				return false;
			}
			return preset.provider_item_id === item.id
				|| item.specs?.every(spec => spec.id === preset.provider_item_spec_id);
		},
		hasPricingRules(item) {
			if (item.specs && item.specs.length) {
				return item.specs.filter(((spec) => spec.has_pricing_rules)).length > 0;
			} else {
				return item.has_pricing_rules;
			}
		},
		async prepareItemGroup() {
			try {
				await this.getItemGroup(this.itemGroupId);
				this.setGroupRequired({groupId: this.itemGroupId, isRequired: this.isRequired});
				this.prepareStockInfo();
			} catch (e) {
				console.error(e);
			}
		},
		async checkFormBuyableAdvancedCap() {
			const service = this.context.selectedService;
            if (!service) {
                return;
            }
			if (this.context.selectedDate) {
				const start = moment.tz(this.context.selectedDate, this.provider.timezone);
				var end;

				if (this.context.selectedEndDate) {
					end = moment.tz(this.context.selectedEndDate, this.provider.timezone);
				} else if (service) {
					end = moment.tz(this.context.selectedDate, this.provider.timezone).add(service.duration_minutes, 'minute');
				}
				await this.getFormBuyableAdvancedCap({serviceId: service.id, start, end});
			} 
		},
		async ensureStockForCartItems() {
			var cartItemsToRemove = {};
			this.cartItems.forEach((cartItem) => {
				const mainItem = this.group?.items.find((item) => item.id === cartItem.itemId);
				if (!mainItem) {
					// the item is not in this group, just skip
					return;
				}
				const specStock = this.stockInfoMap['specs'][cartItem.specId];
				const itemStock = this.stockInfoMap['items'][cartItem.itemId];
				if ((specStock && specStock < cartItem.count) || specStock === 0) {
					cartItemsToRemove['s' + cartItem.specId] = cartItem;
				}
				if ((itemStock && itemStock < cartItem.count) || itemStock === 0) {
					cartItemsToRemove['i' + cartItem.itemId] = cartItem;
				}

				for(var i in this.groupAmountLeftInfo) {
					const group = this.groupAmountLeftInfo[i];
					var shouldCheck = false;
					if (cartItem.specId && group.spec_ids.indexOf(cartItem.specId) >= 0) {
						shouldCheck = true;
					} else if (group.item_ids.indexOf(cartItem.itemId) >= 0) {
						shouldCheck = true;
					}

					if (shouldCheck) {
						const relatedCount = this.cartItems.map((cartItem) => {
							if(cartItem.specId && group.spec_ids.indexOf(cartItem.specId) >= 0) {
								return cartItem.count;
							} else if (!cartItem.specId && group.item_ids.indexOf(cartItem.itemId) >= 0) {
								return cartItem.count;
							}
							return 0;
						}).reduce((memo, current) => {
							return memo + current;
						}, 0);
						if (relatedCount > group.amount_lefts) {
							if (cartItem.specId) {
								cartItemsToRemove['s' + cartItem.specId] = cartItem;
							} else {
								cartItemsToRemove['i' + cartItem.itemId] = cartItem;
							}
						}
					}
				}
			});

			cartItemsToRemove = Object.values(cartItemsToRemove);

			if (cartItemsToRemove.length > 0) {
				const names = cartItemsToRemove.map((cartItem) => {
					const mainItem = this.group.items.find((item) => item.id === cartItem.itemId);
					if (cartItem.specId) {
						return mainItem.specs.find((s) => s.id == cartItem.specId).name;
					} else {
						return mainItem.name;
					}
				});
				this.promptAlert(this.$t('items_not_enough_stock', {itemNames: names.join('<br>')}), () => {
					for(var i in cartItemsToRemove) {
						this.removeCartItem(cartItemsToRemove[i]);
					}
				});
			}
		},
		checkCartItemsAvailability() {
			var itemsToRemove = {};
			this.cartItems.forEach((cartItem) => {
				const mainItem = this.group.items.find((item) => item.id === cartItem.itemId);
				if (!mainItem) {
					// the item is not in this group, just skip
					return;
				}
				if (this.notAvailableDueToPeriod(mainItem) || this.notAvailableDueToWeekday(mainItem)) {
					itemsToRemove[mainItem.id] = mainItem;
				}
				if (cartItem.specId && this.isSpecNotAvailableDueToFixedDate(mainItem, cartItem.specId)) {
					itemsToRemove[mainItem.id] = mainItem;
				}
			});
			if (Object.keys(itemsToRemove).length > 0) {
				this.promptAlert(this.$t('items_not_available_on_selected_date_and_will_be_removed', {itemNames: Object.values(itemsToRemove).map((item) => item.name).join('<br>')}), () => {
					this.removeCartItemByItemIds(Object.keys(itemsToRemove));
				});
			}
		},
		notAvailableDueToPeriod(item) {
			if (!this.context.selectedDate) {
				return false;
			}
			if (item.availablePeriods.length == 0) {
				return false;
			}
			const now = moment(this.context.selectedDate.replaceAll('/', '-')).tz(this.provider.timezone);
			const okPeriod = item.availablePeriods.find((p) => {
				const start = moment.tz(p.start_datetime, 'GMT').tz(this.provider.timezone);
				const end = moment.tz(p.end_datetime, 'GMT').tz(this.provider.timezone);
				return now.isBetween(start, end, null, '[]');
			});
			return !okPeriod;
		},
		notAvailableDueToWeekday(item) {
			if (!this.context.selectedDate) {
				return false;
			}
			if (item.availableWeekdays.length == 0) {
				return false;
			}
			const weekday = moment(this.context.selectedDate.replaceAll('/', '-')).isoWeekday();
			const okWeekday = item.availableWeekdays.find((item) => {
				return item == weekday;
			})
			return !okWeekday;
		},
		isSpecNotAvailableDueToFixedDate(item, specId) {
			if (!this.context.selectedDate) {
				return false;
			}
			const spec = item.specs.find((s) => s.id == specId);

			if (!spec.fixed_date_time) {
				return false;
			}

			const selectedDate = moment.tz(this.context.selectedDate, this.provider.timezone);
			const fixedDateTime = moment.tz(spec.fixed_date_time.replaceAll('-', '/'), 'GMT').tz(this.provider.timezone);
			return !selectedDate.isSame(fixedDateTime);
		},
		notAvailableDueToSpecFixedDate(item) {
			if (!this.context.selectedDate) {
				return false;
			}
			if (item.specs.length == 0) {
				return false;
			}

			const selectedDate = moment.tz(this.context.selectedDate, this.provider.timezone);
			const allNotOnFixedDate = item.specs.filter(spec => {
				if (!spec.fixed_date_time) {
					return false;
				}

				const fixedDateTime = moment.tz(spec.fixed_date_time.replaceAll('-', '/'), 'GMT').tz(this.provider.timezone);
				if (!selectedDate.isSame(fixedDateTime)) {
					return true;
				}
			}).length === item.specs.length;
			return allNotOnFixedDate;
		},
		prepareStockInfo(onResult) {
			if (this.context.selectedService.allow_custom_duration) {
				if (this.context.selectedDate && this.context.selectedEndDate) {
					this.getItemGroupStockInfoInDateRange(this.itemGroupId, this.context.selectedDate, this.context.selectedEndDate, (response, error) => {
						if (response && !error) {
							this.stockInfoMap = response.data;
							onResult();
						}
					});
				}
			} else {
				this.getItemGroupStockInfo(this.itemGroupId, this.context.selectedDate, (response, error) => {
					if (response && !error) {
						this.stockInfoMap = response.data;
						onResult();
					}
				});
			}
		},
		hasReachedGroupLimit(item) {
			if (item.specs && item.specs.length > 0) {
				const groupAmountNoLeftSpecIds = item.specs.map((spec) => {
					for(var i in this.groupAmountLeftInfo) {
						const group = this.groupAmountLeftInfo[i];
						if (group.spec_ids.indexOf(spec.id) >= 0 && group.amount_lefts == 0) {
							return spec.id;
						}
					}
					return null;
				});
				return groupAmountNoLeftSpecIds.filter((specId) => specId).length === item.specs.length;
			} else {
				for(var i in this.groupAmountLeftInfo) {
					const group = this.groupAmountLeftInfo[i];
					if (group.item_ids.indexOf(item.id) >= 0 && group.amount_lefts == 0) {
						return true;
					}
				}
			}
			return false;
		},
		hasSoldOut(item) {
			if (!this.stockInfoMap) {
				return false;
			}

			if (!item.specs || item.specs.length === 0) {
				if(
					typeof this.stockInfoMap['items'][item.id] === 'undefined'
					|| this.stockInfoMap['items'][item.id] === null
				) {
					return false;
				}
				return this.stockInfoMap['items'][item.id] <= 0;
			}

			if (item.specs && item.specs.length > 0) {
				const notSoldOutSpecCount = item.specs.filter((s) => {
					return (typeof this.stockInfoMap['specs'][s.id] === 'undefined') || this.stockInfoMap['specs'][s.id] === null || this.stockInfoMap['specs'][s.id] > 0;
				}).length
				return notSoldOutSpecCount <= 0;
			}

			return false;
		},
		openItem(item) {
			this.openedItem = item;
			this.trackViewItem(item);
		},
		itemImagePath(item) {
			if (item.images && item.images.length > 0) {
				return item.images[0].thumbnail_name;
			}
			if (item.specs) {
				const specWithImage = item.specs.find((s) => !!s.thumbnail_path);
				if (specWithImage) {
					return specWithImage.thumbnail_path;
				}
			}
		},
		showRemainStock(item){
			if (item.show_stock_info) {
				return item.specs.length == 0 ? this.stockInfoMap['items'][item.id] : false;
			}
			return false;
		},
		isTimeLimit (item) {
			return item.availablePeriods.length > 0
				|| item.availableWeekdays.length > 0;
		},
		getAvailableWeekdays (item){
			return item.availableWeekdays;
		},
		getAvailablePeriods (item){
			return item.availablePeriods;
		},
		availablePeriodsToDisplay (period) {
			const start = moment(period.start_datetime).tz(this.provider.timezone);
			const end = moment(period.end_datetime).tz(this.provider.timezone);

			const formattedStart = start.format('Y/M/D');
			const isSameYear = start.format('Y') == end.format('Y');
			const formattedEnd = isSameYear ? end.format('M/D') : end.format('Y/M/D');
			
			const result = formattedStart + this.$t('to') + formattedEnd;
			return result;
		},
		trackViewItem(item) {
			window.tracker.track(() => new EventViewItem(this.provider.currency, item));
		},
		async checkCartItemsStockAndAvailability() {
			await this.checkFormBuyableAdvancedCap();  //get this.groupAmountLeftInfo
			this.prepareStockInfo(() => {
				this.ensureStockForCartItems();
				this.checkCartItemsAvailability();
				this.$forceUpdate();
			});
		}
	}

}
</script>

<style scoped lang="scss">
	.item-container {
		height: 100%;
		margin-bottom: 8px;
		box-shadow: 0px 0px 4px 0px rgba(0,0,0,0.30);
		overflow: hidden;
		border-radius: 4px;
		cursor: pointer;
		display: flex;
		flex-direction: column;
	}

	.grid-wrapper {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		grid-column-gap: 8px;
		grid-row-gap: 8px;

		@media screen and (min-width: 576px) {
			grid-template-columns: repeat(3, 1fr);
			grid-column-gap: 16px;
			grid-row-gap: 16px;
		}
	}
	.item-name {
		overflow: hidden;
		text-overflow: ellipsis;
		line-height: 1.2em;
		display: inline-block;
	}
	
	.remaining-stock{
		font-size: 12px;
		color: #666;
	}

	.image {
		overflow: hidden;
		position: relative;
	}

	/deep/ .item-container .image > div:first-child {
		transform: scale(1);
		overflow: hidden;
		transition-property: transform;
		transition-duration: 0.5s;
	}

	/deep/ .item-container .image:hover {
		& > div:first-child {
			transform: scale(1.2);
		}
	}

	.flex-grow-1 {
		flex-grow: 1;
	}

	.icon-sold-out-container {
		position: absolute;
		top: 0;
		bottom: 0;
		right: 0;
		left: 0;
		display: block;
		background: rgba(255, 255, 255, 0.4);
	}
	
	.icon-sold-out {
		background: rgba(0, 0, 0, 0.7);
		margin-top: calc(50% - 40px);
		border-radius: 4px;
		font-size: 14px;
		color: white;
		display: inline-block;
		position: absolute;
		bottom: 4px;
		right: 4px;
		padding: 2px 10px;
	}
	
	.icon-time-limit {
		background: rgba(19, 169, 211, 0.7);
		border-radius: 4px;
		font-size: 14px;
		color: white;
		display: inline-block;
		position: absolute;
		top: 4px;
		left: 4px;
		padding: 2px 10px;
		font-weight: bold;
	}
</style>

<i18n>
{
	"zh": {
		"only_available_in_periods": "非可訂日",
		"only_available_in_weekday": "非可訂日",
		"not_available": "非可訂日",
		"items_not_available_on_selected_date_and_will_be_removed": "下列商品在選取的日期不可購買，將自購物袋移除。<br>{itemNames}",
		"items_not_enough_stock": "下列商品數量不足以購買，將自購物袋移除。<br>{itemNames}",
		"time_limit": "限",
		"common": "、",
		"to": "-",
		"remaining_label": "剩餘",
		"reach_group_limit": "已售完",
		"included_in_booking_pass": "已於課卡內購買"
	},
	"en": {
		"only_available_in_periods": "No Available Day",
		"only_available_in_weekday": "Not Available Day",
		"not_available": "Not Available Day",
		"items_not_available_on_selected_date_and_will_be_removed": "Below items are not available on date selected and will be removed from shopping bag.<br>{itemNames}",
		"items_not_enough_stock": "No enough stock on date selected and will be removed from shopping bag.<br>{itemNames}",
		"remaining_label": "Remain",
		"time_limit": "Only",
		"common": ",\u00A0",
		"to": "-",
		"reach_group_limit": "Sold Out",
		"included_in_booking_pass": "Included in Booking Pass"
	}
}
</i18n>